import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { RotateCcw } from "lucide-react";
import { ReceivePanel, CycleDetail, tone } from "@/components/returns/ReturnParts";
import { RedeployTab } from "@/components/returns/RedeployTab";
import { CustodyView } from "@/components/returns/CustodyView";

function ReturnsTab({ openCode }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(openCode || null);
  const load = useCallback(() => api.get("/asset-returns/cycles", { params: { q: q || undefined } }).then((r) => setRows(r.data.items)).catch(() => {}), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-4">
      <ReceivePanel onReceived={(c) => { load(); setOpen(c.id); }} />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search return, serial, asset ID, subsidiary…" className="w-80" data-testid="returns-search" />
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="returns-list">{!rows.length && <div className="p-4 text-sm text-slate-500">No returns yet.</div>}
        {rows.map((r) => <button key={r.id} onClick={() => setOpen(r.id)} className="grid w-full grid-cols-1 gap-1 border-b border-slate-100 px-4 py-2.5 text-left text-xs hover:bg-slate-50 md:grid-cols-[160px_1fr_200px_auto]" data-testid={`return-row-${r.id}`}>
          <b>{r.code}</b><span>{r.product} · SN {r.serial} · {r.asset_code}</span><span className="text-slate-600">{r.owner?.entity}</span>
          <span className={`w-fit rounded-full px-2 py-0.5 font-bold ${tone(r.status)}`}>{r.status_label}{r.blocker_count ? ` · ${r.blocker_count} open` : ""}</span></button>)}</div>
      {open && <CycleDetail id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

export default function ReturnsWorkspace() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "returns";
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-8" data-testid="returns-workspace">
      <div className="flex items-center gap-3"><div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-600 text-white"><RotateCcw className="h-5 w-5" /></div>
        <div><h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Returns &amp; Redeployment</h1>
          <p className="text-sm text-slate-500">Recover → Validate → Secure → Refresh → Store → Redeploy, all on the same Golden Asset Record.</p></div></div>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="max-w-full justify-start overflow-x-auto"><TabsTrigger value="returns" data-testid="tab-returns">Receive &amp; validate</TabsTrigger><TabsTrigger value="redeploy" data-testid="tab-redeploy">Redeploy</TabsTrigger><TabsTrigger value="custody" data-testid="tab-custody">Custody &amp; report</TabsTrigger></TabsList>
        <TabsContent value="returns" className="mt-5"><ReturnsTab openCode={sp.get("open")} /></TabsContent>
        <TabsContent value="redeploy" className="mt-5"><RedeployTab /></TabsContent>
        <TabsContent value="custody" className="mt-5"><CustodyView staff /></TabsContent>
      </Tabs>
    </div>
  );
}
